import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  BarChart3,
  Building2,
  ChevronRight,
  ClipboardList,
  Clock3,
  CreditCard,
  Eye,
  IdCard,
  LogOut,
  MapPin,
  Menu,
  Pencil,
  Plus,
  ReceiptText,
  Scale,
  Settings,
  SlidersHorizontal,
  UsersRound,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useSearchParams } from "react-router-dom";
import { Brand } from "../../components/PublicLayout";
import { ConfirmDialog, Dialog } from "../../components/Dialog";
import { Button, EmptyState, Field, NewPasswordField, Notice, SelectField } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { api, currency, dateTR, todayTR } from "../../lib/api";
import type { Quote } from "../../types";
import { TripLocationDialog } from "./TripLocationDialog";

const menu = [
  ["/admin", BarChart3, "Dashboard"],
  ["/admin/filo", MapPin, "Canlı Filo"],
  ["/admin/fiyatlar", SlidersHorizontal, "Fiyat Yönetimi"],
  ["/admin/teklifler", ReceiptText, "Teklifler"],
  ["/admin/firmalar", UsersRound, "Firmalar"],
  ["/admin/soforler", IdCard, "Şoförler"],
  ["/admin/loglar", ClipboardList, "İşlem Logları"],
  ["/admin/ayarlar", Settings, "Ayarlar"],
] as const;
export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen overflow-x-hidden bg-mist lg:grid lg:grid-cols-[260px_1fr]">
      {open && <button type="button" aria-label="Yönetim menüsünü kapat" onClick={() => setOpen(false)} className="fixed inset-0 z-40 bg-navy/55 backdrop-blur-sm lg:hidden"/>}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[min(82vw,280px)] bg-navy p-5 text-white shadow-2xl transition lg:static lg:w-[260px] lg:translate-x-0 lg:shadow-none ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center justify-between">
          <Brand light />
          <button onClick={() => setOpen(false)} className="lg:hidden">
            <X />
          </button>
        </div>
        <nav className="mt-10 space-y-1">
          {menu.map(([to, Icon, label]) => (
            <NavLink
              end={to === "/admin"}
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition ${isActive ? "bg-white text-navy" : "text-slate-300 hover:bg-white/10 hover:text-white"}`
              }
            >
              <Icon size={19} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/5 p-4">
          <p className="truncate text-sm font-semibold">{user?.fullName}</p>
          <p className="truncate text-xs text-slate-400">{user?.email}</p>
          <button
            onClick={logout}
            className="mt-4 flex items-center gap-2 text-xs text-slate-300 hover:text-white"
          >
            <LogOut size={15} /> Çıkış yap
          </button>
        </div>
      </aside>
      <div>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/10 bg-navy px-4 text-white shadow-lg lg:h-20 lg:border-slate-200 lg:bg-white lg:px-8 lg:text-ink lg:shadow-none">
          <button
            onClick={() => setOpen(true)}
            className="rounded-xl border border-white/15 bg-white/10 p-2 lg:hidden"
            aria-label="Yönetim menüsünü aç"
          >
            <Menu />
          </button>
          <p className="hidden text-sm font-semibold text-slate-500 sm:block">
            EHES Yönetim Merkezi
          </p>
          <Link to="/" className="text-sm font-semibold text-coral lg:text-teal">
            Siteyi görüntüle
          </Link>
        </header>
        <main className="p-4 pb-10 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
function Header({ title, text }: { title: string; text: string }) {
  return (
    <div className="mb-5 md:mb-7">
      <h1 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p>
    </div>
  );
}
function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    SUBMITTED: "Yeni",
    REVIEWED: "İncelendi",
    APPROVED: "Onaylandı",
    REJECTED: "Reddedildi",
    ASSIGNED: "Atandı",
    ACCEPTED: "Kabul Edildi",
    IN_PROGRESS: "Yolda",
    COMPLETED: "Tamamlandı",
    CANCELLED: "İptal Edildi",
    PLANNED: "Planlandı",
    PENDING: "Onay Bekliyor",
    ACTIVE: "Aktif",
    SUSPENDED: "Askıda",
  };
  const tones: Record<string, string> = {
    APPROVED: "bg-emerald-50 text-emerald-700",
    ACTIVE: "bg-emerald-50 text-emerald-700",
    COMPLETED: "bg-emerald-50 text-emerald-700",
    IN_PROGRESS: "bg-sky-50 text-sky-700",
    ACCEPTED: "bg-sky-50 text-sky-700",
    REJECTED: "bg-red-50 text-red-700",
    SUSPENDED: "bg-red-50 text-red-700",
    CANCELLED: "bg-slate-100 text-slate-500",
    PENDING: "bg-amber-50 text-amber-700",
    SUBMITTED: "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[value] ?? "bg-slate-100 text-slate-600"}`}>
      {labels[value] ?? value}
    </span>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<{
    totalQuotes: number;
    totalCustomers: number;
    recentQuotes: Quote[];
  } | null>(null);
  useEffect(() => {
    api<typeof data>("/admin/dashboard").then(setData);
  }, []);
  return (
    <>
      <Header
        title="Dashboard"
        text="Platform performansına hızlı bir bakış."
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="card flex items-center justify-between p-6">
          <div>
            <p className="text-sm text-slate-500">Yaklaşan Teklif</p>
            <p className="mt-2 text-4xl font-bold text-navy">
              {data?.totalQuotes ?? "—"}
            </p>
          </div>
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-coral/10 text-coral">
            <ReceiptText />
          </span>
        </div>
        <div className="card flex items-center justify-between p-6">
          <div>
            <p className="text-sm text-slate-500">Toplam Firma</p>
            <p className="mt-2 text-4xl font-bold text-navy">
              {data?.totalCustomers ?? "—"}
            </p>
          </div>
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-teal/10 text-teal">
            <UsersRound />
          </span>
        </div>
      </div>
      <div className="card mt-7 overflow-hidden">
        <div className="flex items-center justify-between p-6">
          <h2 className="text-lg font-bold text-navy">Yaklaşan Teklifler</h2>
          <Link
            to="/admin/teklifler"
            className="flex items-center gap-1 text-sm font-semibold text-teal"
          >
            Tümü <ChevronRight size={16} />
          </Link>
        </div>
        {!data?.recentQuotes.length ? (
          <div className="p-6 pt-0">
            <EmptyState
              title="Henüz teklif yok"
              text="Yeni teklifler burada listelenecek."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Teklif</th>
                  <th>Firma</th>
                  <th>Rota</th>
                  <th>Tutar</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {data.recentQuotes.map((q) => (
                  <tr key={q.id}>
                    <td className="font-semibold text-navy">{q.quoteNumber}</td>
                    <td>{q.user?.companyName}</td>
                    <td>
                      <p className="max-w-48 truncate">{q.originLabel}</p>
                      <p className="text-xs text-slate-400">
                        → {q.destinationLabel.split(",")[0]}
                      </p>
                    </td>
                    <td className="font-semibold">{currency(q.totalPrice)}</td>
                    <td>
                      <Status value={q.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

type PricingData = {
  settings: {
    kmPrice: number | null;
    tonnageCoefficient: number | null;
    minimumFee: number | null;
    currency: string;
  } | null;
  vehicleTypes: Array<{
    id: string;
    slug: string;
    name: string;
    description?: string | null;
    basePrice: number | null;
    isActive: boolean;
    sortOrder: number;
  }>;
};
export function PricingPage() {
  const [data, setData] = useState<PricingData | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleBusy, setVehicleBusy] = useState(false);
  const [vehicleDialogError, setVehicleDialogError] = useState("");
  useEffect(() => {
    api<PricingData>("/admin/pricing").then(setData);
  }, []);
  const change = (
    id: string,
    key: "name" | "description" | "basePrice" | "isActive" | "sortOrder",
    value: string | number | boolean,
  ) =>
    setData((d) =>
      d
        ? {
            ...d,
            vehicleTypes: d.vehicleTypes.map((v) =>
              v.id === id ? { ...v, [key]: value } : v,
            ),
          }
        : d,
    );
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!data) return;
    setBusy(true);
    const fd = new FormData(e.currentTarget);
    try {
      await api("/admin/pricing", {
        method: "PUT",
        body: JSON.stringify({
          kmPrice: Number(fd.get("kmPrice")),
          tonnageCoefficient: Number(fd.get("tonnageCoefficient")),
          minimumFee: Number(fd.get("minimumFee")),
          currency: String(fd.get("currency")),
          vehicleTypes: data.vehicleTypes.map((v) => ({
            id: v.id,
            name: v.name,
            description: v.description?.trim() || null,
            basePrice: Number(v.basePrice),
            isActive: v.isActive,
            sortOrder: v.sortOrder,
          })),
        }),
      });
      setMsg("Fiyatlandırma kaydedildi ve teklif ekranına yansıtıldı.");
    } finally {
      setBusy(false);
    }
  };
  const addVehicle = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setVehicleBusy(true);
    setMsg("");
    setVehicleDialogError("");
    try {
      const response = await api<{ vehicleType: PricingData["vehicleTypes"][number] }>("/admin/vehicle-types", {
        method: "POST",
        body: JSON.stringify({
          name: String(form.get("name")),
          description: String(form.get("description")).trim() || null,
          basePrice: Number(form.get("basePrice")),
          isActive: form.get("isActive") === "on",
        }),
      });
      setData((current) => current && { ...current, vehicleTypes: [...current.vehicleTypes, response.vehicleType] });
      formElement.reset();
      setVehicleDialogOpen(false);
      setMsg("Yeni araç tipi oluşturuldu.");
    } catch (error) {
      setVehicleDialogError(error instanceof Error ? error.message : "Araç tipi oluşturulamadı.");
    } finally {
      setVehicleBusy(false);
    }
  };
  if (!data) return <p>Yükleniyor…</p>;
  return (
    <>
      <Header
        title="Fiyat Yönetimi"
        text="Teklif motorunun kullandığı tüm fiyatları tek yerden yönetin."
      />
      <form onSubmit={save} className="space-y-7">
        {msg && <Notice tone="success">{msg}</Notice>}
        <div className="card p-6">
          <h2 className="text-lg font-bold text-navy">Genel Tarife</h2>
          <p className="mt-1 text-sm text-slate-500">
            Toplam = mesafe × KM fiyatı + araç fiyatı + tonaj × tonaj katsayısı.
            Minimum ücret alt sınırdır.
          </p>
          <div className="mt-6 grid gap-5 md:grid-cols-4">
            <Field
              name="kmPrice"
              label="KM Fiyatı"
              type="number"
              min="0"
              step="0.01"
              defaultValue={data.settings?.kmPrice ?? ""}
              required
            />
            <Field
              name="tonnageCoefficient"
              label="Tonaj Katsayısı"
              type="number"
              min="0"
              step="0.01"
              defaultValue={data.settings?.tonnageCoefficient ?? ""}
              required
            />
            <Field
              name="minimumFee"
              label="Minimum Taşıma Ücreti"
              type="number"
              min="0"
              step="0.01"
              defaultValue={data.settings?.minimumFee ?? ""}
              required
            />
            <Field
              name="currency"
              label="Para Birimi"
              maxLength={3}
              defaultValue={data.settings?.currency ?? "TRY"}
              required
            />
          </div>
        </div>
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between gap-4 p-6">
            <div>
              <h2 className="text-lg font-bold text-navy">Araç Tipi Fiyatları</h2>
              <p className="mt-1 text-sm text-slate-500">Araçları, açıklamalarını ve teklif ekranındaki sıralarını yönetin.</p>
            </div>
            <Button type="button" variant="secondary" onClick={() => { setVehicleDialogError(""); setVehicleDialogOpen(true); }}><Plus size={17} /> Yeni Araç Tipi</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Araç</th>
                  <th>Açıklama</th>
                  <th>Fiyat</th>
                  <th>Sıra</th>
                  <th>Kullanım</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicleTypes.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <input
                        className="input max-w-xs"
                        value={v.name}
                        onChange={(e) => change(v.id, "name", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="input min-w-64"
                        value={v.description ?? ""}
                        onChange={(e) => change(v.id, "description", e.target.value)}
                        placeholder="Araç açıklaması"
                      />
                    </td>
                    <td>
                      <input
                        className="input max-w-52"
                        type="number"
                        min="0"
                        step="0.01"
                        value={v.basePrice ?? ""}
                        onChange={(e) =>
                          change(v.id, "basePrice", Number(e.target.value))
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="input max-w-24"
                        type="number"
                        min="0"
                        step="1"
                        value={v.sortOrder}
                        onChange={(e) => change(v.id, "sortOrder", Number(e.target.value))}
                      />
                    </td>
                    <td>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={v.isActive}
                          onChange={(e) =>
                            change(v.id, "isActive", e.target.checked)
                          }
                        />{" "}
                        Aktif
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Button loading={busy}>Fiyatları Kaydet</Button>
      </form>
      <Dialog
        open={vehicleDialogOpen}
        onClose={() => setVehicleDialogOpen(false)}
        title="Yeni Araç Tipi"
        description="Araç tipi aktifse ve fiyatı tanımlıysa firma teklif ekranında kullanılabilir."
        closeDisabled={vehicleBusy}
      >
        <form className="space-y-5" onSubmit={addVehicle}>
          {vehicleDialogError && <Notice tone="error">{vehicleDialogError}</Notice>}
          <Field name="name" label="Araç Adı" required />
          <Field name="description" label="Açıklama (opsiyonel)" maxLength={300} />
          <Field name="basePrice" label="Sabit Araç Fiyatı" type="number" min="0" step="0.01" required />
          <label className="flex items-center gap-3 rounded-2xl bg-mist p-4 text-sm font-semibold text-navy">
            <input name="isActive" type="checkbox" defaultChecked /> Tekliflerde aktif olarak kullan
          </label>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" disabled={vehicleBusy} onClick={() => setVehicleDialogOpen(false)}>Vazgeç</Button>
            <Button loading={vehicleBusy}>Araç Tipi Oluştur</Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

type Driver = {
  status: "PENDING" | "ACTIVE" | "SUSPENDED";
  licenseNumber?: string | null;
  createdAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    phone?: string | null;
    _count?: { driverAssignments: number };
    driverVehicles?: DriverVehicle[];
  };
};

type DriverVehicle = {
  id: string;
  plateNumber: string;
  brand: string;
  model: string;
  modelYear?: number | null;
  capacityTonnes?: number | null;
  isActive: boolean;
  vehicleType: { id: string; name: string };
};

type DriverDetail = {
  driver: Driver & { updatedAt: string };
  assignments: Array<{
    id: string;
    status: "ASSIGNED" | "ACCEPTED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
    assignedAt: string;
    updatedAt: string;
    chargedAmount: number;
    assignedBy: { fullName: string; email: string };
    trip?: {
      id: string;
      status: string;
      startedAt?: string | null;
      completedAt?: string | null;
      vehicle?: DriverVehicle | null;
      _count: { locationEvents: number };
    } | null;
    quote: {
      id: string;
      quoteNumber: string;
      status: string;
      originLabel: string;
      originLat: number;
      originLon: number;
      destinationLabel: string;
      destinationLat: number;
      destinationLon: number;
      distanceKm: number;
      durationMinutes: number;
      tonnage: number;
      loadingDate: string;
      totalPrice: number;
      organization: { id: string; name: string; address: string };
      vehicleType: { id: string; name: string };
    };
  }>;
};

type AdminQuote = {
  id: string;
  quoteNumber: string;
  organizationId: string;
  createdByUserId: string;
  vehicleTypeId: string;
  originLabel: string;
  originLat: number;
  originLon: number;
  destinationLabel: string;
  destinationLat: number;
  destinationLon: number;
  distanceKm: number;
  durationMinutes: number;
  tonnage: number;
  loadingDate: string;
  totalPrice: number;
  pricingSnapshot: {
    kmPrice?: number | null;
    vehiclePrice?: number | null;
    tonnageCoefficient?: number | null;
    minimumFee?: number | null;
    currency?: string;
    formulaVersion?: string;
  };
  routeGeometry?: unknown;
  status: string;
  createdAt: string;
  updatedAt: string;
  organization: {
    id: string;
    name: string;
    address: string;
    taxNumber?: string | null;
    wallet?: { balance: number; currency: string } | null;
  };
  createdBy: { id: string; fullName: string; email: string; phone?: string | null };
  user: { fullName: string; email: string; companyName: string };
  vehicleType: { id: string; slug: string; name: string; description?: string | null };
  assignment?: {
    id: string;
    driverId: string;
    status: string;
    assignedAt: string;
    chargedAmount: number;
    driver: { fullName: string; email: string; phone?: string | null };
    trip?: {
      id: string;
      status: string;
      startedAt?: string | null;
      completedAt?: string | null;
    } | null;
  } | null;
};

type PendingConfirmation = {
  title: string;
  description: string;
  confirmLabel: string;
  action: () => Promise<void>;
};

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="rounded-2xl bg-mist p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p><div className="mt-2 text-sm font-semibold leading-6 text-navy">{value}</div></div>;
}

const quoteStatusLabel = (status: string) => ({ SUBMITTED: "Yeni", REVIEWED: "İncelendi", APPROVED: "Onaylandı", REJECTED: "Reddedildi" })[status] ?? status;

export function QuotesPage() {
  const [searchParams] = useSearchParams();
  const requestedQuoteId = searchParams.get("detay");
  const [quotes, setQuotes] = useState<AdminQuote[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<PricingData["vehicleTypes"]>([]);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [selectedQuote, setSelectedQuote] = useState<AdminQuote | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [editingQuote, setEditingQuote] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const load = () =>
    api<{ quotes: AdminQuote[] }>("/admin/quotes").then((d) => setQuotes(d.quotes));
  useEffect(() => {
    void load();
    api<{ drivers: Driver[] }>("/admin/drivers").then((d) =>
      setDrivers(d.drivers),
    );
    api<PricingData>("/admin/pricing").then((d) => setVehicleTypes(d.vehicleTypes));
  }, []);
  const openDetail = useCallback(async (id: string) => {
    setDetailBusy(true);
    setEditingQuote(false);
    setMessage("");
    try {
      const response = await api<{ quote: AdminQuote }>(`/admin/quotes/${id}`);
      setSelectedQuote(response.quote);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "Teklif detayı yüklenemedi.");
    } finally {
      setDetailBusy(false);
    }
  }, []);
  useEffect(() => {
    if (requestedQuoteId) void openDetail(requestedQuoteId);
  }, [openDetail, requestedQuoteId]);
  const askStatusChange = (quote: AdminQuote, value: string) => {
    if (value === quote.status) return;
    setConfirmation({
      title: "Teklif durumunu değiştir",
      description: `${quote.quoteNumber} numaralı teklif “${quoteStatusLabel(value)}” durumuna geçirilecek.${value !== "APPROVED" && quote.assignment ? " Mevcut tamamlanmamış şoför ataması iptal edilecek." : ""}`,
      confirmLabel: "Durumu Güncelle",
      action: async () => {
        await api(`/admin/quotes/${quote.id}/status`, { method: "PATCH", body: JSON.stringify({ status: value }) });
        await load();
        if (selectedQuote?.id === quote.id) await openDetail(quote.id);
        setMessageTone("success");
        setMessage("Teklif durumu güncellendi.");
      },
    });
  };
  const askAssignment = (quote: AdminQuote, driverId: string) => {
    if (!driverId) return;
    const driver = drivers.find((item) => item.user.id === driverId);
    setConfirmation({
      title: quote.assignment ? "Şoför atamasını değiştir" : "Şoför ata",
      description: `${quote.quoteNumber} numaralı teklif ${driver?.user.fullName ?? "seçilen şoföre"} atanacak.${quote.assignment ? " Bu yeniden atamada firmadan ikinci kez kredi düşülmez." : ` İlk atama olduğu için ${currency(quote.totalPrice)} firma kredisinden düşülecek.`}`,
      confirmLabel: quote.assignment ? "Atamayı Değiştir" : "Şoförü Ata",
      action: async () => {
        await api(`/admin/quotes/${quote.id}/assignment`, { method: "PUT", body: JSON.stringify({ driverId }) });
        await load();
        if (selectedQuote?.id === quote.id) await openDetail(quote.id);
        setMessageTone("success");
        setMessage("Şoför ataması güncellendi.");
      },
    });
  };
  const saveQuote = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedQuote) return;
    const form = new FormData(event.currentTarget);
    const financiallyLocked = (selectedQuote.assignment?.chargedAmount ?? 0) > 0;
    const payload = {
      originLabel: String(form.get("originLabel")),
      originLat: financiallyLocked ? selectedQuote.originLat : Number(form.get("originLat")),
      originLon: financiallyLocked ? selectedQuote.originLon : Number(form.get("originLon")),
      destinationLabel: String(form.get("destinationLabel")),
      destinationLat: financiallyLocked ? selectedQuote.destinationLat : Number(form.get("destinationLat")),
      destinationLon: financiallyLocked ? selectedQuote.destinationLon : Number(form.get("destinationLon")),
      distanceKm: financiallyLocked ? selectedQuote.distanceKm : Number(form.get("distanceKm")),
      durationMinutes: financiallyLocked ? selectedQuote.durationMinutes : Number(form.get("durationMinutes")),
      vehicleTypeId: financiallyLocked ? selectedQuote.vehicleTypeId : String(form.get("vehicleTypeId")),
      tonnage: financiallyLocked ? selectedQuote.tonnage : Number(form.get("tonnage")),
      loadingDate: String(form.get("loadingDate")),
    };
    setConfirmation({
      title: "Teklif değişikliklerini kaydet",
      description: `${selectedQuote.quoteNumber} numaralı teklif güncellenecek.${financiallyLocked ? " Tahsilat yapıldığı için fiyatı etkileyen alanlar korunacak." : " Fiyat, güncel tarife üzerinden sunucuda yeniden hesaplanacak."}`,
      confirmLabel: "Değişiklikleri Kaydet",
      action: async () => {
        const response = await api<{ quote: AdminQuote }>(`/admin/quotes/${selectedQuote.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        setSelectedQuote(response.quote);
        setEditingQuote(false);
        await load();
        setMessageTone("success");
        setMessage("Teklif bilgileri ve fiyatı güncellendi.");
      },
    });
  };
  const runConfirmation = async () => {
    if (!confirmation) return;
    setConfirmBusy(true);
    setMessage("");
    try {
      await confirmation.action();
      setConfirmation(null);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "İşlem tamamlanamadı.");
      setConfirmation(null);
    } finally {
      setConfirmBusy(false);
    }
  };
  return (
    <>
      <Header
        title="Yaklaşan Teklifler"
        text="Bugün ve sonrasındaki firma tekliflerini inceleyin, onaylayın ve aktif şoföre atayın."
      />
      {message && !selectedQuote && <Notice tone={messageTone} className="mb-5" onClose={() => setMessage("")}>{message}</Notice>}
      <div className="card overflow-hidden">
        {quotes.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Yaklaşan teklif bulunamadı"
              text="Bugün veya sonrasına planlanan firma teklifleri burada görünecek."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Teklif</th>
                  <th>Firma / Oluşturan</th>
                  <th>Araç / Tonaj</th>
                  <th>Rota</th>
                  <th>Fiyat</th>
                  <th>Durum</th>
                  <th>Şoför</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id}>
                    <td>
                      <p className="font-semibold text-navy">{q.quoteNumber}</p>
                      <p className="text-xs text-slate-400">
                        {dateTR(q.createdAt)}
                      </p>
                    </td>
                    <td>
                      <p>{q.user?.companyName}</p>
                      <p className="text-xs text-slate-400">
                        {q.user?.fullName}
                      </p>
                    </td>
                    <td>
                      {q.vehicleType.name}
                      <p className="text-xs text-slate-400">{q.tonnage} ton</p>
                    </td>
                    <td>
                      <p className="max-w-52 truncate">{q.originLabel}</p>
                      <p className="max-w-52 truncate text-xs text-slate-400">
                        → {q.destinationLabel}
                      </p>
                    </td>
                    <td className="font-semibold">{currency(q.totalPrice)}</td>
                    <td>
                      <Status value={q.status} />
                    </td>
                    <td>
                      <p className="text-sm font-medium text-navy">{q.assignment?.driver.fullName ?? "Atanmadı"}</p>
                      {q.assignment && <p className="mt-1 text-xs text-slate-400">{q.assignment.status}</p>}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="ghost" disabled={detailBusy} onClick={() => void openDetail(q.id)}><Eye size={16} /> Detay</Button>
                        {q.assignment?.trip && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setSelectedTripId(q.assignment?.trip?.id ?? null)}
                            aria-label={`${q.quoteNumber} teklifini haritada takip et`}
                          >
                            <MapPin size={16}/> Haritada Gör
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={Boolean(selectedQuote)}
        onClose={() => { setSelectedQuote(null); setEditingQuote(false); }}
        title={selectedQuote ? `${selectedQuote.quoteNumber} Teklif Detayı` : "Teklif Detayı"}
        description="Rota, fiyatlandırma, firma ve şoför ataması bilgilerini tek ekranda inceleyin."
        size="xl"
      >
        {selectedQuote && message && <Notice tone={messageTone} className="mb-5" onClose={() => setMessage("")}>{message}</Notice>}
        {selectedQuote && (
          editingQuote ? (
            <form className="space-y-6" onSubmit={saveQuote}>
              {(selectedQuote.assignment?.chargedAmount ?? 0) > 0 && (
                <Notice tone="warning" title="Finansal alanlar kilitli">
                  Bu teklif için {currency(selectedQuote.assignment?.chargedAmount ?? 0)} tahsil edildi. Finansal tutarlılık için araç, tonaj, koordinat, mesafe ve süre alanları kilitlidir; adres etiketleri ve yükleme tarihi düzenlenebilir.
                </Notice>
              )}
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-4 rounded-2xl border p-5">
                  <h3 className="font-bold text-navy">Çıkış Noktası</h3>
                  <Field name="originLabel" label="Adres" defaultValue={selectedQuote.originLabel} required />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field name="originLat" label="Enlem" type="number" step="any" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.originLat} required />
                    <Field name="originLon" label="Boylam" type="number" step="any" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.originLon} required />
                  </div>
                </div>
                <div className="space-y-4 rounded-2xl border p-5">
                  <h3 className="font-bold text-navy">Varış Noktası</h3>
                  <Field name="destinationLabel" label="Adres" defaultValue={selectedQuote.destinationLabel} required />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field name="destinationLat" label="Enlem" type="number" step="any" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.destinationLat} required />
                    <Field name="destinationLon" label="Boylam" type="number" step="any" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.destinationLon} required />
                  </div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <SelectField name="vehicleTypeId" label="Araç Tipi" disabled={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.vehicleTypeId}>
                  {vehicleTypes.filter((item) => item.isActive || item.id === selectedQuote.vehicleTypeId).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </SelectField>
                <Field name="tonnage" label="Tonaj" type="number" min="0.1" step="0.1" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.tonnage} required />
                <Field name="distanceKm" label="Mesafe (km)" type="number" min="0.1" step="0.01" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.distanceKm} required />
                <Field name="durationMinutes" label="Süre (dakika)" type="number" min="1" step="1" readOnly={(selectedQuote.assignment?.chargedAmount ?? 0) > 0} defaultValue={selectedQuote.durationMinutes} required />
                <Field name="loadingDate" label="Yükleme Tarihi" type="date" min={todayTR()} defaultValue={selectedQuote.loadingDate.slice(0, 10)} required />
              </div>
              <div className="flex justify-end gap-3 border-t pt-5">
                <Button type="button" variant="ghost" onClick={() => setEditingQuote(false)}>Vazgeç</Button>
                <Button>Kaydetmek İçin Onayla</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-7">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3"><Status value={selectedQuote.status} /><span className="text-sm text-slate-500">Son güncelleme: {new Date(selectedQuote.updatedAt).toLocaleString("tr-TR")}</span></div>
                <Button type="button" variant="secondary" onClick={() => setEditingQuote(true)}><Pencil size={16} /> Teklifi Düzenle</Button>
              </div>
              <div className="grid gap-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-5 lg:grid-cols-2">
                <SelectField label="Teklif Durumu" value={selectedQuote.status} onChange={(event) => askStatusChange(selectedQuote, event.target.value)}>
                  <option value="SUBMITTED">Yeni</option>
                  <option value="REVIEWED">İncelendi</option>
                  <option value="APPROVED">Onaylandı</option>
                  <option value="REJECTED">Reddedildi</option>
                </SelectField>
                <SelectField label="Atanan Şoför" value={selectedQuote.assignment?.driverId ?? ""} disabled={selectedQuote.status !== "APPROVED"} onChange={(event) => askAssignment(selectedQuote, event.target.value)}>
                  <option value="">{selectedQuote.status === "APPROVED" ? "Şoför seçin" : "Önce teklifi onaylayın"}</option>
                  {drivers.filter((driver) => driver.status === "ACTIVE").map((driver) => <option key={driver.user.id} value={driver.user.id}>{driver.user.fullName}</option>)}
                </SelectField>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <DetailItem label="Firma" value={<>{selectedQuote.organization.name}<p className="font-normal text-slate-500">{selectedQuote.organization.address}</p></>} />
                <DetailItem label="Oluşturan" value={<>{selectedQuote.createdBy.fullName}<p className="font-normal text-slate-500">{selectedQuote.createdBy.email}</p></>} />
                <DetailItem label="Yükleme Tarihi" value={dateTR(selectedQuote.loadingDate)} />
                <DetailItem label="Toplam Fiyat" value={currency(selectedQuote.totalPrice, selectedQuote.pricingSnapshot.currency ?? "TRY")} />
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border p-5">
                  <div className="flex items-center gap-2 font-bold text-navy"><MapPin size={18} className="text-coral" /> Rota</div>
                  <div className="mt-5 space-y-4 text-sm">
                    <div><p className="text-xs font-semibold uppercase text-slate-400">Nereden</p><p className="mt-1 font-semibold text-navy">{selectedQuote.originLabel}</p><p className="text-xs text-slate-400">{selectedQuote.originLat}, {selectedQuote.originLon}</p></div>
                    <div><p className="text-xs font-semibold uppercase text-slate-400">Nereye</p><p className="mt-1 font-semibold text-navy">{selectedQuote.destinationLabel}</p><p className="text-xs text-slate-400">{selectedQuote.destinationLat}, {selectedQuote.destinationLon}</p></div>
                  </div>
                  <a className="mt-5 inline-flex text-sm font-semibold text-teal" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&origin=${selectedQuote.originLat},${selectedQuote.originLon}&destination=${selectedQuote.destinationLat},${selectedQuote.destinationLon}`}>Rotayı haritada aç</a>
                </div>
                <div className="rounded-2xl border p-5">
                  <div className="font-bold text-navy">Taşıma Bilgileri</div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <DetailItem label="Araç" value={selectedQuote.vehicleType.name} />
                    <DetailItem label="Tonaj" value={`${selectedQuote.tonnage} ton`} />
                    <DetailItem label="Mesafe" value={<span className="flex items-center gap-2"><Scale size={15} /> {selectedQuote.distanceKm.toLocaleString("tr-TR")} km</span>} />
                    <DetailItem label="Tahmini Süre" value={<span className="flex items-center gap-2"><Clock3 size={15} /> {Math.floor(selectedQuote.durationMinutes / 60)} sa {selectedQuote.durationMinutes % 60} dk</span>} />
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border p-5">
                <h3 className="font-bold text-navy">Fiyat Kırılımı</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="KM Fiyatı" value={selectedQuote.pricingSnapshot.kmPrice == null ? "—" : currency(selectedQuote.pricingSnapshot.kmPrice)} />
                  <DetailItem label="Araç Sabit Fiyatı" value={selectedQuote.pricingSnapshot.vehiclePrice == null ? "—" : currency(selectedQuote.pricingSnapshot.vehiclePrice)} />
                  <DetailItem label="Tonaj Katsayısı" value={selectedQuote.pricingSnapshot.tonnageCoefficient == null ? "—" : currency(selectedQuote.pricingSnapshot.tonnageCoefficient)} />
                  <DetailItem label="Minimum Ücret" value={selectedQuote.pricingSnapshot.minimumFee == null ? "—" : currency(selectedQuote.pricingSnapshot.minimumFee)} />
                </div>
              </div>
              <div className="rounded-2xl border p-5">
                <h3 className="font-bold text-navy">Şoför ve Tahsilat</h3>
                {selectedQuote.assignment ? <div className="mt-4 grid gap-3 sm:grid-cols-3"><DetailItem label="Şoför" value={<>{selectedQuote.assignment.driver.fullName}<p className="font-normal text-slate-500">{selectedQuote.assignment.driver.phone ?? selectedQuote.assignment.driver.email}</p></>} /><DetailItem label="Atama Durumu" value={selectedQuote.assignment.status} /><DetailItem label="Tahsil Edilen" value={currency(selectedQuote.assignment.chargedAmount)} /></div> : <p className="mt-3 text-sm text-slate-500">Henüz şoför atanmamış ve firmadan tahsilat yapılmamış.</p>}
                {selectedQuote.assignment?.trip && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-4"
                    onClick={() => setSelectedTripId(selectedQuote.assignment?.trip?.id ?? null)}
                  >
                    <MapPin size={16}/> Canlı Konumu Haritada Gör
                  </Button>
                )}
              </div>
            </div>
          )
        )}
      </Dialog>
      <TripLocationDialog
        tripId={selectedTripId}
        audience="admin"
        onClose={() => setSelectedTripId(null)}
      />
      <ConfirmDialog
        open={Boolean(confirmation)}
        title={confirmation?.title ?? "İşlemi onayla"}
        description={confirmation?.description ?? ""}
        confirmLabel={confirmation?.confirmLabel}
        busy={confirmBusy}
        onClose={() => setConfirmation(null)}
        onConfirm={runConfirmation}
      />
    </>
  );
}

type Customer = {
  id: string;
  ownerUserId?: string;
  fullName: string;
  email: string;
  phone?: string;
  companyName?: string;
  address: string;
  taxNumber?: string | null;
  wallet?: { balance: number; currency: string } | null;
  memberCount: number;
  createdAt: string;
  _count: { quotes: number };
};
type WalletDetail = {
  organization: { id: string; name: string };
  wallet: { balance: number; currency: string; updatedAt: string };
  transactions: Array<{
    id: string;
    type: "CREDIT_TOPUP" | "ASSIGNMENT_DEBIT";
    amount: number;
    balanceAfter: number;
    description?: string | null;
    createdAt: string;
    quote?: { quoteNumber: string } | null;
    assignment?: { driver: { fullName: string } } | null;
    actor?: { fullName: string; email: string } | null;
  }>;
};
type OrganizationOverview = {
  members: Array<{
    id: string;
    createdAt: string;
    user: { id: string; fullName: string; email: string; phone?: string | null };
    role: { name: string; isOwner: boolean; permissions: string[] };
  }>;
  upcomingQuotes: Array<{
    id: string;
    quoteNumber: string;
    status: string;
    originLabel: string;
    destinationLabel: string;
    loadingDate: string;
    totalPrice: number;
    vehicleType: { name: string };
    assignment?: { status: string; driver: { fullName: string } } | null;
  }>;
  summary: { upcomingQuoteCount: number; approvedQuoteCount: number; assignedQuoteCount: number; upcomingTotal: number };
};
type CreditRequest = { organizationId: string; organizationName: string; amount: number; note: string; formElement: HTMLFormElement };
export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [wallet, setWallet] = useState<WalletDetail | null>(null);
  const [overview, setOverview] = useState<OrganizationOverview | null>(null);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogError, setCompanyDialogError] = useState("");
  const [creditRequest, setCreditRequest] = useState<CreditRequest | null>(null);
  const [creditBusy, setCreditBusy] = useState(false);
  const load = async () => {
    const response = await api<{ customers: Customer[] }>("/admin/customers");
    setCustomers(response.customers);
    return response.customers;
  };
  const openWallet = async (id: string) => {
    const response = await api<WalletDetail>(`/admin/organizations/${id}/wallet`);
    setWallet(response);
    return response;
  };
  useEffect(() => {
    void load();
  }, []);
  const createCompany = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setCreatingCompany(true);
    setMessage("");
    setCompanyDialogError("");
    try {
      await api("/admin/organizations", {
        method: "POST",
        body: JSON.stringify({
          companyName: String(form.get("companyName")),
          address: String(form.get("address")),
          taxNumber: String(form.get("taxNumber")).trim() || null,
          fullName: String(form.get("fullName")),
          email: String(form.get("email")),
          password: String(form.get("password")),
        }),
      });
      formElement.reset();
      await load();
      setCompanyDialogOpen(false);
      setMessageTone("success");
      setMessage("Firma hesabı ve sıfır bakiyeli cüzdanı oluşturuldu.");
    } catch (error) {
      setCompanyDialogError(error instanceof Error ? error.message : "Firma hesabı oluşturulamadı.");
    } finally {
      setCreatingCompany(false);
    }
  };
  const openCustomerDetail = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditingCustomer(false);
    setMessage("");
    setWallet(null);
    setOverview(null);
    setDetailBusy(true);
    try {
      const [, organizationOverview] = await Promise.all([
        openWallet(customer.id),
        api<OrganizationOverview>(`/admin/organizations/${customer.id}/overview`),
      ]);
      setOverview(organizationOverview);
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "Firma detayı yüklenemedi.");
    } finally {
      setDetailBusy(false);
    }
  };
  const saveCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCustomer) return;
    const form = new FormData(event.currentTarget);
    setSavingCustomer(true);
    setMessage("");
    try {
      await api(`/admin/organizations/${selectedCustomer.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          companyName: String(form.get("companyName")),
          address: String(form.get("address")),
          taxNumber: String(form.get("taxNumber")).trim() || null,
          fullName: String(form.get("fullName")),
          email: String(form.get("email")),
          phone: String(form.get("phone")).trim() || null,
        }),
      });
      const refreshed = await load();
      setSelectedCustomer(refreshed.find((customer) => customer.id === selectedCustomer.id) ?? null);
      setEditingCustomer(false);
      setMessageTone("success");
      setMessage("Firma ve yönetici bilgileri güncellendi.");
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "Firma bilgileri güncellenemedi.");
    } finally {
      setSavingCustomer(false);
    }
  };
  const addCredit = (event: FormEvent<HTMLFormElement>, id: string) => {
    event.preventDefault();
    setMessage("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setCreditRequest({
      organizationId: id,
      organizationName: customers.find((customer) => customer.id === id)?.companyName ?? "Firma",
      amount: Number(form.get("amount")),
      note: String(form.get("note")),
      formElement,
    });
  };
  const confirmCredit = async () => {
    if (!creditRequest) return;
    setCreditBusy(true);
    try {
      await api(`/admin/organizations/${creditRequest.organizationId}/credits`, {
        method: "POST",
        body: JSON.stringify({
          amount: creditRequest.amount,
          note: creditRequest.note,
        }),
      });
      creditRequest.formElement.reset();
      const [refreshed] = await Promise.all([load(), openWallet(creditRequest.organizationId)]);
      setSelectedCustomer(refreshed.find((customer) => customer.id === creditRequest.organizationId) ?? selectedCustomer);
      setCreditRequest(null);
      setMessageTone("success");
      setMessage("Firma kredisi tanımlandı.");
    } catch (error) {
      setMessageTone("error");
      setMessage(
        error instanceof Error ? error.message : "Kredi tanımlanamadı.",
      );
      setCreditRequest(null);
    } finally {
      setCreditBusy(false);
    }
  };
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Header
          title="Firmalar ve Krediler"
          text="Firma bilgilerini, kullanılabilir krediyi ve tüm cüzdan hareketlerini yönetin."
        />
        <Button type="button" onClick={() => { setCompanyDialogError(""); setCompanyDialogOpen(true); }}><Building2 size={17} /> Firma / Yönetici Ekle</Button>
      </div>
      {message && !selectedCustomer && <Notice tone={messageTone} className="mb-5" onClose={() => setMessage("")}>{message}</Notice>}
      <div className="card overflow-hidden">
        {customers.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Firma bulunamadı"
              text="Yeni firma kayıtları burada listelenecek."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th>Firma Sahibi</th>
                  <th>Kullanıcı / Teklif</th>
                  <th>Kredi</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <p className="font-semibold text-navy">
                        {customer.companyName ?? "—"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {dateTR(customer.createdAt)}
                      </p>
                    </td>
                    <td>
                      <p>{customer.fullName}</p>
                      <p className="text-xs text-slate-400">{customer.email}</p>
                      <p className="text-xs text-slate-400">
                        {customer.phone ?? "—"}
                      </p>
                    </td>
                    <td>
                      {customer.memberCount} kullanıcı
                      <p className="text-xs text-slate-400">
                        {customer._count.quotes} yaklaşan teklif
                      </p>
                    </td>
                    <td>
                      <p className="font-semibold text-navy">
                        {currency(
                          customer.wallet?.balance ?? 0,
                          customer.wallet?.currency ?? "TRY",
                        )}
                      </p>
                    </td>
                    <td>
                      <Button type="button" variant="ghost" disabled={detailBusy} onClick={() => void openCustomerDetail(customer)}><Eye size={16}/> Detay</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={Boolean(selectedCustomer)}
        onClose={() => { setSelectedCustomer(null); setWallet(null); setOverview(null); setEditingCustomer(false); setMessage(""); }}
        title={selectedCustomer ? `${selectedCustomer.companyName} Firma Detayı` : "Firma Detayı"}
        description="Firma, yönetici, kredi ve cüzdan hareketlerini tek ekrandan yönetin."
        size="xl"
        closeDisabled={savingCustomer || creditBusy}
      >
        {selectedCustomer && message && <Notice tone={messageTone} className="mb-5" onClose={() => setMessage("")}>{message}</Notice>}
        {selectedCustomer && (editingCustomer ? (
          <form className="space-y-5" onSubmit={saveCustomer}>
            <div className="grid gap-4 md:grid-cols-2">
              <Field name="companyName" label="Firma Adı" defaultValue={selectedCustomer.companyName} required />
              <Field name="taxNumber" label="Vergi No (opsiyonel)" inputMode="numeric" pattern="[0-9]{10,11}" defaultValue={selectedCustomer.taxNumber ?? ""} />
            </div>
            <Field name="address" label="Firma Adresi" minLength={5} defaultValue={selectedCustomer.address} required />
            <div className="grid gap-4 md:grid-cols-3">
              <Field name="fullName" label="Firma Yöneticisi" defaultValue={selectedCustomer.fullName} required />
              <Field name="email" label="Yönetici E-postası" type="email" defaultValue={selectedCustomer.email} required />
              <Field name="phone" label="Telefon" type="tel" defaultValue={selectedCustomer.phone ?? ""} />
            </div>
            <div className="flex justify-end gap-3 border-t pt-5">
              <Button type="button" variant="ghost" disabled={savingCustomer} onClick={() => setEditingCustomer(false)}>Vazgeç</Button>
              <Button loading={savingCustomer}>Değişiklikleri Kaydet</Button>
            </div>
          </form>
        ) : (
          <div className="space-y-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div><p className="text-sm text-slate-500">Firma kaydı</p><p className="font-semibold text-navy">{dateTR(selectedCustomer.createdAt)}</p></div>
              <Button type="button" variant="secondary" onClick={() => { setMessage(""); setEditingCustomer(true); }}><Pencil size={16}/> Bilgileri Düzenle</Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <DetailItem label="Firma" value={selectedCustomer.companyName ?? "—"}/>
              <DetailItem label="Yönetici" value={<>{selectedCustomer.fullName}<p className="font-normal text-slate-500">{selectedCustomer.email}</p></>}/>
              <DetailItem label="İletişim" value={selectedCustomer.phone ?? "Tanımlanmadı"}/>
              <DetailItem label="Vergi No" value={selectedCustomer.taxNumber ?? "Tanımlanmadı"}/>
            </div>
            <DetailItem label="Firma Adresi" value={selectedCustomer.address}/>
            <div className="grid gap-5 lg:grid-cols-[.8fr_1.2fr]">
              <div className="rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-teal/10 text-teal"><CreditCard size={20}/></span><div><p className="text-sm text-slate-500">Kullanılabilir kredi</p><p className="text-2xl font-bold text-navy">{currency(wallet?.wallet.balance ?? selectedCustomer.wallet?.balance ?? 0, wallet?.wallet.currency ?? selectedCustomer.wallet?.currency ?? "TRY")}</p></div></div>
                <form className="mt-5 space-y-4 border-t pt-5" onSubmit={(event) => addCredit(event, selectedCustomer.id)}>
                  <Field name="amount" label="Yüklenecek Tutar" type="number" min="0.01" step="0.01" required />
                  <Field name="note" label="İşlem Açıklaması" minLength={2} maxLength={300} required />
                  <Notice tone="warning">Kredi yükleme kalıcı bir cüzdan hareketi oluşturur ve işlem onayından sonra uygulanır.</Notice>
                  <Button className="w-full">Kredi Yüklemeyi Onayla</Button>
                </form>
              </div>
              <div className="rounded-2xl border border-slate-200 p-5">
                <h3 className="font-bold text-navy">Firma Özeti</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2"><DetailItem label="Kullanıcı" value={`${selectedCustomer.memberCount} kişi`}/><DetailItem label="Yaklaşan Teklif" value={`${selectedCustomer._count.quotes} adet`}/></div>
                <p className="mt-5 text-sm leading-6 text-slate-500">Firma yöneticisi kullanıcıları ve teklifleri firma yetkileri üzerinden yönetir. Kredi yalnızca ana yönetici tarafından eklenebilir.</p>
              </div>
            </div>
          </div>
        ))}
        {selectedCustomer && !editingCustomer && overview && <div className="mt-7 grid gap-7">
          <section className="rounded-2xl border border-slate-200 p-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><h3 className="font-bold text-navy">Yaklaşan Operasyon Özeti</h3><p className="mt-1 text-sm text-slate-500">Bugün ve sonrasındaki yükleme kayıtları</p></div>
              <p className="text-lg font-bold text-navy">{currency(overview.summary.upcomingTotal)}</p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <DetailItem label="Yaklaşan Teklif" value={`${overview.summary.upcomingQuoteCount} adet`}/>
              <DetailItem label="Onaylanan" value={`${overview.summary.approvedQuoteCount} adet`}/>
              <DetailItem label="Şoför Atanan" value={`${overview.summary.assignedQuoteCount} adet`}/>
            </div>
          </section>
          <section className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="border-b p-5"><h3 className="font-bold text-navy">Firma Kullanıcıları</h3><p className="mt-1 text-sm text-slate-500">Firma üyeleri ve yetki rolleri</p></div>
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Kullanıcı</th><th>İletişim</th><th>Rol</th><th>Üyelik</th></tr></thead>
                <tbody>{overview.members.map(member => <tr key={member.id}>
                  <td><p className="font-semibold text-navy">{member.user.fullName}</p><p className="text-xs text-slate-400">{member.user.email}</p></td>
                  <td>{member.user.phone ?? "—"}</td>
                  <td><p>{member.role.name}</p><p className="text-xs text-slate-400">{member.role.isOwner ? "Firma sahibi" : `${member.role.permissions.length} yetki`}</p></td>
                  <td>{dateTR(member.createdAt)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </section>
          <section className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="border-b p-5"><h3 className="font-bold text-navy">Yaklaşan Teklifler</h3><p className="mt-1 text-sm text-slate-500">Yükleme tarihine göre en yakından uzağa sıralanır.</p></div>
            {overview.upcomingQuotes.length === 0 ? <div className="p-5"><EmptyState title="Yaklaşan teklif yok" text="Bugün veya sonrasına planlanmış teklif bulunmuyor."/></div> : <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Teklif</th><th>Rota</th><th>Yükleme</th><th>Araç</th><th>Şoför</th><th>Tutar</th></tr></thead>
                <tbody>{overview.upcomingQuotes.map(quote => <tr key={quote.id}>
                  <td><p className="font-semibold text-navy">{quote.quoteNumber}</p><Status value={quote.status}/></td>
                  <td><p className="max-w-56 truncate">{quote.originLabel}</p><p className="max-w-56 truncate text-xs text-slate-400">→ {quote.destinationLabel}</p></td>
                  <td>{dateTR(quote.loadingDate)}</td>
                  <td>{quote.vehicleType.name}</td>
                  <td>{quote.assignment?.driver.fullName ?? "Atanmadı"}<p className="text-xs text-slate-400">{quote.assignment && <Status value={quote.assignment.status}/>}</p></td>
                  <td className="font-semibold">{currency(quote.totalPrice)}</td>
                </tr>)}</tbody>
              </table>
            </div>}
          </section>
        </div>}
        {selectedCustomer && !editingCustomer && wallet && <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200">
          <div className="border-b p-5"><h3 className="font-bold text-navy">Kredi Hareketleri</h3><p className="mt-1 text-sm text-slate-500">Son {wallet.transactions.length} cüzdan işlemi</p></div>
          {wallet.transactions.length === 0 ? (
            <div className="p-5">
              <EmptyState
                title="Hareket bulunamadı"
                text="Kredi yükleme ve iş atama tahsilatları burada listelenecek."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>İşlem</th>
                    <th>Teklif / Şoför</th>
                    <th>İşlemi Yapan</th>
                    <th>Tutar</th>
                    <th>Kalan</th>
                  </tr>
                </thead>
                <tbody>
                  {wallet.transactions.map((item) => (
                    <tr key={item.id}>
                      <td>{dateTR(item.createdAt)}</td>
                      <td>
                        {item.type === "CREDIT_TOPUP"
                          ? "Kredi yükleme"
                          : "İş atama tahsilatı"}
                        <p className="text-xs text-slate-400">
                          {item.description}
                        </p>
                      </td>
                      <td>
                        {item.quote?.quoteNumber ?? "—"}
                        <p className="text-xs text-slate-400">
                          {item.assignment?.driver.fullName}
                        </p>
                      </td>
                      <td>
                        {item.actor?.fullName ?? "Sistem"}
                        <p className="text-xs text-slate-400">
                          {item.actor?.email}
                        </p>
                      </td>
                      <td
                        className={`font-semibold ${item.amount < 0 ? "text-red-600" : "text-emerald-600"}`}
                      >
                        {item.amount > 0 ? "+" : ""}
                        {currency(item.amount, wallet.wallet.currency)}
                      </td>
                      <td>
                        {currency(item.balanceAfter, wallet.wallet.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>}
      </Dialog>
      <Dialog
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        title="Firma ve Yönetici Ekle"
        description="Firma sahibi kullanıcısı, tüm firma izinleri ve sıfır bakiyeli cüzdan birlikte oluşturulur."
        closeDisabled={creatingCompany}
      >
        <form className="space-y-5" onSubmit={createCompany}>
          {companyDialogError && <Notice tone="error">{companyDialogError}</Notice>}
          <div className="grid gap-4 md:grid-cols-2">
            <Field name="companyName" label="Firma Adı" required />
            <Field name="taxNumber" label="Vergi No (opsiyonel)" inputMode="numeric" pattern="[0-9]{10,11}" />
          </div>
          <Field name="address" label="Firma Adresi" minLength={5} required />
          <div className="grid gap-4 md:grid-cols-2">
            <Field name="fullName" label="Firma Yöneticisi Ad Soyad" required />
            <Field name="email" label="Yönetici E-postası" type="email" autoComplete="off" required />
          </div>
          <NewPasswordField name="password" label="İlk Şifre" autoComplete="new-password" required />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" disabled={creatingCompany} onClick={() => setCompanyDialogOpen(false)}>Vazgeç</Button>
            <Button loading={creatingCompany}>Firma Hesabı Oluştur</Button>
          </div>
        </form>
      </Dialog>
      <ConfirmDialog
        open={Boolean(creditRequest)}
        title="Firma kredisini tanımla"
        description={creditRequest ? `${creditRequest.organizationName} hesabına ${currency(creditRequest.amount)} kredi eklenecek. Bu işlem değiştirilemez bir cüzdan hareketi oluşturur. Açıklama: ${creditRequest.note}` : ""}
        confirmLabel="Krediyi Tanımla"
        busy={creditBusy}
        onClose={() => setCreditRequest(null)}
        onConfirm={confirmCredit}
      />
    </>
  );
}

export function DriversPage() {
  const [searchParams] = useSearchParams();
  const requestedDriverId = searchParams.get("detay");
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<DriverDetail | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [driverDetailBusy, setDriverDetailBusy] = useState(false);
  const [driverDetailError, setDriverDetailError] = useState("");
  const [message, setMessage] = useState("");
  const [creatingDriver, setCreatingDriver] = useState(false);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [driverDialogError, setDriverDialogError] = useState("");
  const load = () =>
    api<{ drivers: Driver[] }>("/admin/drivers").then((d) =>
      setDrivers(d.drivers),
    );
  useEffect(() => {
    void load();
  }, []);
  const openDriverDetail = useCallback(async (userId: string) => {
    setDriverDetailBusy(true);
    setDriverDetailError("");
    try {
      setSelectedDriver(await api<DriverDetail>(`/admin/drivers/${userId}/assignments`));
    } catch (error) {
      setDriverDetailError(error instanceof Error ? error.message : "Şoför detayları yüklenemedi.");
    } finally {
      setDriverDetailBusy(false);
    }
  }, []);
  useEffect(() => {
    if (requestedDriverId) void openDriverDetail(requestedDriverId);
  }, [openDriverDetail, requestedDriverId]);
  const createDriver = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setCreatingDriver(true);
    setMessage("");
    setDriverDialogError("");
    try {
      await api("/admin/drivers", {
        method: "POST",
        body: JSON.stringify({
          fullName: String(form.get("fullName")),
          phone: String(form.get("phone")).trim() || null,
          licenseNumber: String(form.get("licenseNumber")).trim() || null,
          email: String(form.get("email")),
          password: String(form.get("password")),
          status: String(form.get("status")),
        }),
      });
      formElement.reset();
      await load();
      setDriverDialogOpen(false);
      setMessage("Şoför hesabı oluşturuldu.");
    } catch (error) {
      setDriverDialogError(error instanceof Error ? error.message : "Şoför hesabı oluşturulamadı.");
    } finally {
      setCreatingDriver(false);
    }
  };
  const change = async (userId: string, status: Driver["status"]) => {
    await api(`/admin/drivers/${userId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    await load();
  };
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Header
          title="Şoförler"
          text="Bağımsız kayıt olan şoförleri onaylayın veya askıya alın."
        />
        <Button type="button" onClick={() => { setDriverDialogError(""); setDriverDialogOpen(true); }}><Plus size={17} /> Şoför Ekle</Button>
      </div>
      {message && <Notice tone="success" className="mb-5" onClose={() => setMessage("")}>{message}</Notice>}
      <div className="card overflow-hidden">
        {drivers.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Şoför bulunamadı"
              text="Mobil uygulamadan kayıt olan şoförler burada listelenecek."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Şoför</th>
                  <th>Telefon</th>
                  <th>Ehliyet</th>
                  <th>Kayıt</th>
                  <th>Atanan Teklif</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <tr key={driver.user.id}>
                    <td>
                      <p className="font-semibold text-navy">
                        {driver.user.fullName}
                      </p>
                      <p className="text-xs text-slate-400">
                        {driver.user.email}
                      </p>
                    </td>
                    <td>{driver.user.phone ?? "—"}</td>
                    <td>{driver.licenseNumber ?? "—"}</td>
                    <td>{dateTR(driver.createdAt)}</td>
                    <td><span className="font-semibold text-navy">{driver.user._count?.driverAssignments ?? 0}</span><span className="ml-1 text-xs text-slate-400">atama</span></td>
                    <td>
                      <select
                        className="rounded-lg border px-2 py-1.5 text-xs"
                        value={driver.status}
                        onChange={(e) =>
                          change(
                            driver.user.id,
                            e.target.value as Driver["status"],
                          )
                        }
                      >
                        <option value="PENDING">Onay Bekliyor</option>
                        <option value="ACTIVE">Aktif</option>
                        <option value="SUSPENDED">Askıda</option>
                      </select>
                    </td>
                    <td><Button type="button" variant="ghost" disabled={driverDetailBusy} onClick={() => void openDriverDetail(driver.user.id)}><Eye size={16}/> Detay</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {driverDetailError && !selectedDriver && <Notice tone="error" className="mt-5" onClose={() => setDriverDetailError("")}>{driverDetailError}</Notice>}
      <Dialog
        open={Boolean(selectedDriver)}
        onClose={() => { setSelectedTripId(null); setSelectedDriver(null); setDriverDetailError(""); }}
        title={selectedDriver ? `${selectedDriver.driver.user.fullName} — Şoför Detayı` : "Şoför Detayı"}
        description="Şoför profili ile kendisine atanmış tekliflerin operasyon ve rota bilgilerini inceleyin."
        size="xl"
      >
        {selectedDriver && (
          <div className="space-y-7">
            {driverDetailError && <Notice tone="error" onClose={() => setDriverDetailError("")}>{driverDetailError}</Notice>}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <DetailItem label="Şoför" value={<>{selectedDriver.driver.user.fullName}<p className="font-normal text-slate-500">{selectedDriver.driver.user.email}</p></>}/>
              <DetailItem label="Telefon" value={selectedDriver.driver.user.phone ?? "Tanımlanmadı"}/>
              <DetailItem label="Ehliyet No" value={selectedDriver.driver.licenseNumber ?? "Tanımlanmadı"}/>
              <DetailItem label="Durum" value={<Status value={selectedDriver.driver.status}/>}/>
              <DetailItem label="Toplam Atama" value={`${selectedDriver.assignments.length} teklif`}/>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <DetailItem label="Aktif Operasyon" value={`${selectedDriver.assignments.filter(item => ["ASSIGNED", "ACCEPTED", "IN_PROGRESS"].includes(item.status)).length} adet`}/>
              <DetailItem label="Yolda" value={`${selectedDriver.assignments.filter(item => item.status === "IN_PROGRESS").length} adet`}/>
              <DetailItem label="Toplam Mesafe" value={`${selectedDriver.assignments.reduce((total, item) => total + item.quote.distanceKm, 0).toLocaleString("tr-TR")} km`}/>
              <DetailItem label="Yaklaşan İş Tutarı" value={currency(selectedDriver.assignments.reduce((total, item) => total + item.quote.totalPrice, 0))}/>
            </div>
            <section className="rounded-2xl border border-slate-200 p-5">
              <div className="flex flex-wrap items-end justify-between gap-3"><div><h3 className="font-bold text-navy">Kayıtlı Araçlar</h3><p className="mt-1 text-sm text-slate-500">Şoförün mobil uygulamada kullanabileceği araçlar</p></div><p className="text-xs text-slate-400">{selectedDriver.driver.user.driverVehicles?.filter(vehicle => vehicle.isActive).length ?? 0} aktif araç</p></div>
              {!selectedDriver.driver.user.driverVehicles?.length ? <div className="mt-4"><EmptyState title="Kayıtlı araç yok" text="Şoför henüz bir araç eklememiş."/></div> : <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {selectedDriver.driver.user.driverVehicles.map(vehicle => <div key={vehicle.id} className="rounded-2xl bg-mist p-4">
                  <div className="flex items-start justify-between gap-3"><div><p className="font-bold text-navy">{vehicle.plateNumber}</p><p className="mt-1 text-sm text-slate-500">{vehicle.brand} {vehicle.model}{vehicle.modelYear ? ` · ${vehicle.modelYear}` : ""}</p></div><Status value={vehicle.isActive ? "ACTIVE" : "SUSPENDED"}/></div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500"><span>{vehicle.vehicleType.name}</span><span>{vehicle.capacityTonnes ? `${vehicle.capacityTonnes} ton kapasite` : "Kapasite belirtilmedi"}</span></div>
                </div>)}
              </div>}
            </section>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b pb-4">
              <div><h3 className="text-lg font-bold text-navy">Yaklaşan Atamalar</h3><p className="mt-1 text-sm text-slate-500">Bugün ve sonrası, yükleme tarihine göre sıralanır.</p></div>
              <p className="text-xs text-slate-400">Şoför kaydı: {dateTR(selectedDriver.driver.createdAt)}</p>
            </div>
            {selectedDriver.assignments.length === 0 ? (
              <EmptyState title="Atanmış teklif bulunamadı" text="Bu şoföre bir teklif atandığında rota ve operasyon detayları burada görünecek."/>
            ) : (
              <div className="space-y-5">
                {selectedDriver.assignments.map((assignment) => (
                  <article key={assignment.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="flex flex-wrap items-start justify-between gap-4 border-b bg-slate-50/70 p-5">
                      <div><div className="flex flex-wrap items-center gap-2"><h4 className="font-bold text-navy">{assignment.quote.quoteNumber}</h4><Status value={assignment.status}/><Status value={assignment.quote.status}/></div><p className="mt-2 text-sm text-slate-500">{assignment.quote.organization.name} · {assignment.quote.vehicleType.name} · {assignment.quote.tonnage} ton</p></div>
                      <div className="text-right"><p className="text-xl font-bold text-navy">{currency(assignment.quote.totalPrice)}</p><p className="mt-1 text-xs text-slate-400">Atama: {new Date(assignment.assignedAt).toLocaleString("tr-TR")}</p></div>
                    </div>
                    <div className="grid gap-5 p-5 lg:grid-cols-[1.25fr_.75fr]">
                      <div>
                        <div className="flex items-start gap-3"><span className="mt-1 h-3 w-3 shrink-0 rounded-full bg-teal"/><div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Çıkış</p><p className="mt-1 font-semibold text-navy">{assignment.quote.originLabel}</p></div></div>
                        <div className="ml-[5px] h-7 border-l border-dashed border-slate-300"/>
                        <div className="flex items-start gap-3"><span className="mt-1 h-3 w-3 shrink-0 rounded-full bg-coral"/><div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Varış</p><p className="mt-1 font-semibold text-navy">{assignment.quote.destinationLabel}</p></div></div>
                        <a className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&origin=${assignment.quote.originLat},${assignment.quote.originLon}&destination=${assignment.quote.destinationLat},${assignment.quote.destinationLon}`}><MapPin size={16}/> Rotayı haritada aç</a>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <DetailItem label="Yükleme" value={dateTR(assignment.quote.loadingDate)}/>
                        <DetailItem label="Mesafe" value={`${assignment.quote.distanceKm.toLocaleString("tr-TR")} km`}/>
                        <DetailItem label="Tahmini Süre" value={`${Math.floor(assignment.quote.durationMinutes / 60)} sa ${assignment.quote.durationMinutes % 60} dk`}/>
                        <DetailItem label="Tahsilat" value={currency(assignment.chargedAmount)}/>
                        <DetailItem label="Sefer" value={assignment.trip ? <Status value={assignment.trip.status}/> : "Oluşturulmadı"}/>
                        <DetailItem
                          label="Konum Kaydı"
                          value={
                            <>
                              <span>{assignment.trip?._count.locationEvents ?? 0} nokta</span>
                              {assignment.trip && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="mt-2 min-h-9 w-full px-3 py-1.5 text-xs"
                                  onClick={() => setSelectedTripId(assignment.trip?.id ?? null)}
                                >
                                  <MapPin size={15} /> Haritada Gör
                                </Button>
                              )}
                            </>
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-between gap-2 border-t px-5 py-3 text-xs text-slate-400"><span>Atayan: {assignment.assignedBy.fullName}</span><span>{assignment.trip?.vehicle ? `Araç: ${assignment.trip.vehicle.plateNumber} · ${assignment.trip.vehicle.brand} ${assignment.trip.vehicle.model}` : "Sefer aracı henüz seçilmedi"}</span><span>Firma adresi: {assignment.quote.organization.address}</span></div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </Dialog>
      <TripLocationDialog
        tripId={selectedTripId}
        onClose={() => setSelectedTripId(null)}
      />
      <Dialog
        open={driverDialogOpen}
        onClose={() => setDriverDialogOpen(false)}
        title="Yeni Şoför Hesabı"
        description="Şoförün giriş bilgilerini ve başlangıç durumunu belirleyin."
        closeDisabled={creatingDriver}
      >
        <form className="space-y-5" onSubmit={createDriver}>
          {driverDialogError && <Notice tone="error">{driverDialogError}</Notice>}
          <div className="grid gap-4 md:grid-cols-2">
            <Field name="fullName" label="Ad Soyad" required />
            <Field name="phone" label="Telefon (opsiyonel)" type="tel" />
            <Field name="licenseNumber" label="Ehliyet No (opsiyonel)" minLength={3} />
            <Field name="email" label="E-posta" type="email" autoComplete="off" required />
            <NewPasswordField name="password" label="İlk Şifre" autoComplete="new-password" required />
            <SelectField name="status" label="Başlangıç Durumu" defaultValue="ACTIVE">
              <option value="ACTIVE">Aktif</option>
              <option value="PENDING">Onay Bekliyor</option>
              <option value="SUSPENDED">Askıda</option>
            </SelectField>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" disabled={creatingDriver} onClick={() => setDriverDialogOpen(false)}>Vazgeç</Button>
            <Button loading={creatingDriver}>Şoför Hesabı Oluştur</Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

type AuditLog = {
  id: string;
  requestId: string;
  actorRole?: string | null;
  actorLabel?: string | null;
  action: string;
  entityType: string;
  entityId?: string | null;
  outcome: "SUCCESS" | "FAILURE";
  method: string;
  path: string;
  metadata?: unknown;
  createdAt: string;
  actor?: { fullName: string; email: string } | null;
  organization?: { id: string; name: string } | null;
};
export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [action, setAction] = useState("");
  const [outcome, setOutcome] = useState("");
  const load = async () => {
    const params = new URLSearchParams();
    if (action) params.set("action", action);
    if (outcome) params.set("outcome", outcome);
    const data = await api<{ logs: AuditLog[] }>(`/admin/audit-logs?${params}`);
    setLogs(data.logs);
  };
  useEffect(() => {
    api<{ logs: AuditLog[] }>('/admin/audit-logs').then(data => setLogs(data.logs));
  }, []);
  return (
    <>
      <Header
        title="İşlem Logları"
        text="Kimlik, firma, kredi, teklif, şoför ve ayar işlemlerinin denetim kayıtlarını inceleyin."
      />
      <form
        className="card mb-6 flex flex-wrap items-end gap-4 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
      >
        <Field
          label="İşlem ara"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          placeholder="Örn. CREDIT, QUOTE, AUTH"
        />
        <label>
          <span className="mb-2 block text-sm font-semibold text-ink">
            Sonuç
          </span>
          <select
            className="input"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="">Tümü</option>
            <option value="SUCCESS">Başarılı</option>
            <option value="FAILURE">Başarısız</option>
          </select>
        </label>
        <Button>Filtrele</Button>
      </form>
      <div className="card overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Log bulunamadı"
              text="Sistem işlemleri burada listelenecek."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Zaman</th>
                  <th>İşlem</th>
                  <th>Aktör</th>
                  <th>Firma</th>
                  <th>Hedef</th>
                  <th>İstek</th>
                  <th>Detay</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      {dateTR(log.createdAt)}
                      <p
                        className={`text-xs font-semibold ${log.outcome === "SUCCESS" ? "text-emerald-600" : "text-red-600"}`}
                      >
                        {log.outcome === "SUCCESS" ? "Başarılı" : "Başarısız"}
                      </p>
                    </td>
                    <td className="font-semibold text-navy">
                      {log.action}
                      <p className="text-xs font-normal text-slate-400">
                        {log.actorRole}
                      </p>
                    </td>
                    <td>
                      {log.actor?.fullName ?? log.actorLabel ?? "Sistem"}
                      <p className="text-xs text-slate-400">
                        {log.actor?.email}
                      </p>
                    </td>
                    <td>{log.organization?.name ?? "—"}</td>
                    <td>
                      {log.entityType}
                      <p className="max-w-40 truncate text-xs text-slate-400">
                        {log.entityId ?? "—"}
                      </p>
                    </td>
                    <td>
                      <code className="text-xs">
                        {log.method} {log.path}
                      </code>
                      <p className="max-w-40 truncate text-xs text-slate-400">
                        {log.requestId}
                      </p>
                    </td>
                    <td>
                      <code className="block max-w-64 whitespace-pre-wrap break-all text-xs text-slate-500">
                        {log.metadata ? JSON.stringify(log.metadata) : "—"}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

type AppSettings = {
  companyName: string;
  supportEmail?: string | null;
  supportPhone?: string | null;
  address?: string | null;
};
export function SettingsPage() {
  const [data, setData] = useState<AppSettings | null>(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api<{ settings: AppSettings }>("/admin/settings").then((d) =>
      setData(d.settings),
    );
  }, []);
  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await api("/admin/settings", {
      method: "PUT",
      body: JSON.stringify({
        companyName: fd.get("companyName"),
        supportEmail: fd.get("supportEmail") || null,
        supportPhone: fd.get("supportPhone") || null,
        address: fd.get("address") || null,
      }),
    });
    setMsg("Ayarlar kaydedildi.");
  };
  if (!data) return <p>Yükleniyor…</p>;
  return (
    <>
      <Header title="Ayarlar" text="Kurumsal iletişim bilgilerini yönetin." />
      <form onSubmit={save} className="card max-w-2xl space-y-5 p-7">
        {msg && <Notice tone="success">{msg}</Notice>}
        <Field
          name="companyName"
          label="Firma Adı"
          defaultValue={data.companyName}
          required
        />
        <Field
          name="supportEmail"
          label="Destek E-postası"
          type="email"
          defaultValue={data.supportEmail ?? ""}
        />
        <Field
          name="supportPhone"
          label="Destek Telefonu"
          defaultValue={data.supportPhone ?? ""}
        />
        <Field name="address" label="Adres" defaultValue={data.address ?? ""} />
        <Button>Ayarları Kaydet</Button>
      </form>
    </>
  );
}
